import { FC, useMemo } from 'react';

import { isNonNullish } from 'remeda';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';

import { AddLevelButton } from './addLevelButton/AddLevelButton';
import { LevelsTableActionsColumn } from './levelsTableColumns/LevelsTableActionsColumn';

import css from './LevelsPage.module.css';

import { levelsService } from 'v2/api/levelsService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { TableColumn } from 'v2/types';
import { LevelModel } from 'v2/types/LevelModel';
import { formatNumber } from 'v2/utils';

export const LevelsPage: FC = () => {
  const { data, isLoading, mutate } = useSWR('getLevels', levelsService.getLevels);

  const columns: TableColumn<LevelModel>[] = useMemo(
    () => [
      { key: 'userLevel', label: 'User Level', style: { width: '10%' }, render: (row) => <Chip label={row.level} /> },
      {
        key: 'levelPoints',
        label: 'Level Points',
        align: 'center',
        render: (row) => formatNumber(row.levelPoints),
      },
      {
        key: 'dailyBonusPercent',
        label: 'Daily Bonus, %',
        align: 'center',
        render: (row) => <span className={css.levelsTableDailyBonus}>{row.dailyBonusPercent}%</span>,
      },
      {
        key: 'referralBonusPercent',
        label: 'Referral Bonus, %',
        align: 'center',
        render: (row) => <span className={css.levelsTableReferralBonus}>{row.referralBonusPercent}%</span>,
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => (
          <LevelsTableActionsColumn level={row} onRevalidate={mutate} isLast={(data?.length ?? 1) - 1 === row.level} />
        ),
      },
    ],
    [data, mutate]
  );

  return (
    <PageLayout className={css.levelsPage} overflow="hidden">
      <PageTitle action={isNonNullish(data) && <AddLevelButton nextLevel={data.length} onSuccess={mutate} />}>
        Levels Settings
      </PageTitle>

      {isLoading ? <Loader /> : <DataTable columns={columns} data={data} />}
    </PageLayout>
  );
};
